import React, { useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CustomText, { textStyles } from "../../components/CustomText";
import { useTranslation } from "react-i18next";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";



// Icon Imports
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import BusinessIcon from "@mui/icons-material/Business";
import ArchitectureIcon from "@mui/icons-material/Architecture";
import CallIcon from "@mui/icons-material/Call";
import DesignServicesIcon from "@mui/icons-material/DesignServices";
import CodeIcon from "@mui/icons-material/Code";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import EventIcon from "@mui/icons-material/Event";
import GavelIcon from "@mui/icons-material/Gavel";
import GroupIcon from "@mui/icons-material/Group";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import MedicalInformationIcon from "@mui/icons-material/MedicalInformation";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import CampaignIcon from "@mui/icons-material/Campaign";
import HandshakeIcon from "@mui/icons-material/Handshake";
import FolderSharedIcon from "@mui/icons-material/FolderShared";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import AssistantIcon from "@mui/icons-material/Assistant";

const industries = [
  { title: "Accountants", icon: <AccountBalanceIcon /> },
  { title: "Agencies", icon: <BusinessIcon /> },
  { title: "Architecture", icon: <ArchitectureIcon /> },
  { title: "Call Centers", icon: <CallIcon /> },
  { title: "Designers", icon: <DesignServicesIcon /> },
  { title: "Developers", icon: <CodeIcon /> },
  { title: "Ecommerce", icon: <ShoppingCartIcon /> },
  { title: "Event Planning", icon: <EventIcon /> },
  { title: "Government", icon: <GavelIcon /> },
  { title: "Human Resources", icon: <GroupIcon /> },
  { title: "Insurance", icon: <AssignmentIndIcon /> },
  { title: "Interior Design", icon: <MeetingRoomIcon /> },
  { title: "Legal Services", icon: <GavelIcon /> },
  { title: "Management Consultants", icon: <LocalHospitalIcon /> },
  { title: "Medical Billing", icon: <MedicalInformationIcon /> },
  { title: "Medical Staffing", icon: <MedicalInformationIcon /> },
  { title: "Nonprofits", icon: <VolunteerActivismIcon /> },
  { title: "PR", icon: <CampaignIcon /> },
  { title: "Professional Services", icon: <HandshakeIcon /> },
  { title: "Project Management", icon: <FolderSharedIcon /> },
  { title: "Publishing", icon: <MenuBookIcon /> },
  { title: "Real Estate", icon: <HomeWorkIcon /> },
  { title: "Recruiting", icon: <PersonSearchIcon /> },
  { title: "Staffing", icon: <WorkOutlineIcon /> },
  { title: "Virtual Assistants", icon: <AssistantIcon /> },
];

const OfficeWorkers = () => {
  const CARDS_PREVIEW_COUNT = 6;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const visibleIndustries = isMobile && !showAll ? industries.slice(0, CARDS_PREVIEW_COUNT) : industries;

  return (
    <Box sx={{ px: 3, py: 6, mt: 1, textAlign: "center" }}>
      <CustomText
        style={{
          ...textStyles.h1,
          marginBottom: isMobile ? "0rem" : "1rem",
          marginTop: isMobile ? "0rem" : "1rem",
          fontSize: isMobile ? "30px" : "36px",
          textAlign: "center",
        }}
      >
        {t("office.title")}
      </CustomText>

      <CustomText
        style={{
          ...textStyles.body1,
          color: "#868282",
          maxWidth: "910px",
          margin: "0 auto",
          marginTop: "10px",
          fontSize: "24px",
          lineHeight: "120%",
          textAlign: "center",
        }}
      >
        {t("office.description")}
      </CustomText>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: { xs: 2, md: 3 },
          px: { xs: 1, sm: 5, lg: 28 },
          mt: 3,
        }}
      >
        {visibleIndustries.map((item, index) => (
          <Box
            key={index}
            sx={{
              width: { xs: "46%", md: "30%" },
              minHeight: { xs: 100, md: 120 },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid #DFDFDF",
              borderRadius: "12px",
              p: { xs: 1, md: 2 },
              cursor: "pointer",
              transition: "0.3s",
              "&:hover": {
                transform: "scale(1.03)",
              },
            }}
          >
            <Box sx={{ color: "#1976d2", mb: 0.2 }}>{item.icon}</Box>
            <Typography sx={{ fontWeight: 700, color: "#003149", fontSize: { xs: "13px", md: "18px" }, textAlign: "center" }}>
              {t(`office.industries.${item.title}`)}
            </Typography>
          </Box>
        ))}
      </Box>

      {isMobile && industries.length > CARDS_PREVIEW_COUNT && (
        <Box
          onClick={() => setShowAll((prev) => !prev)}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            mt: 4,
            cursor: "pointer",
            color: "#1976d2",
            fontWeight: 600,
            fontSize: "16px",
            userSelect: "none",
            "&:hover": { opacity: 0.75 },
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: "16px", color: "inherit" }}>
            {showAll ? t("common.viewLess") : t("common.viewMore")}
          </Typography>
          <KeyboardArrowDownIcon
            sx={{
              transition: "transform 0.3s",
              transform: showAll ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default OfficeWorkers;
